import { useState } from 'react'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import useModalClose from 'hooks/useModalClose'
import { ColorVariant, CursorPointer, Flex, colors } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Card, CardBody } from 'components/Card/Card'
import { IFoodException } from 'types/foodLogistics/foodException.interface'

interface IProps {
  exception: IFoodException
  closeModal: () => void
  onResolve: (actionKey: string, comment: string) => void
}

export const ResolveExceptionModal = ({ exception, closeModal, onResolve }: IProps) => {
  const [action, setAction] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  useModalClose(closeModal)

  const isLow = exception.sev === 'Low'
  const disabled = !isLow && !action

  return (
    <Modal black width='560px'>
      <Flex column gap='1rem' width='100%'>
        <Flex between width='100%' center>
          <TitleText weight='bold'>Resolve — {exception.type}</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <Flex gap='0.5rem' center>
          <StatusPill status={exception.sev === 'Critical' ? 'danger' : exception.sev === 'High' ? 'accent' : 'info'}>{exception.sev}</StatusPill>
          <StatusPill status='info'>{`Recommendation: ${exception.prov}`}</StatusPill>
        </Flex>
        <OtherText grey>{exception.root}</OtherText>
        {exception.rec && (
          <Alert
            title='Recommended resolution'
            description={exception.rec}
            type={AlertType.WARNING}
            colorVariant={ColorVariant.PRIMARY}
          />
        )}
        {!isLow && (
          <Flex column gap='0.5rem' width='100%'>
            <OtherText grey>Corrective action (mandatory for {exception.sev})</OtherText>
            {exception.actions.map((a) => (
              <Card
                key={a.k}
                onClick={() => setAction(a.k)}
                fullBorder={action === a.k ? colors.orange : undefined}
                hoverBorder
                noScale
              >
                <CardBody>
                  <BodyText weight='700'>{a.label}</BodyText>
                  <OtherText grey>{a.detail}</OtherText>
                </CardBody>
              </Card>
            ))}
          </Flex>
        )}
        <Input
          type='textarea'
          labelText={isLow ? 'Comment (closes the exception)' : 'Comment (optional)'}
          value={comment}
          onChange={(e: any) => setComment(e.target.value)}
          placeholder='Please provide a reason…'
          nomarg
        />
        <Flex gap='0.5rem' justcenter width='100%'>
          <Button variant='secondary' onClick={closeModal}>Cancel</Button>
          <Button variant='primary' disabled={disabled} onClick={() => onResolve(isLow ? 'COMMENT' : action!, comment)}>
            {isLow ? 'Close' : 'Execute'}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
